import { fetchFieldFacets } from 'api/WorkflowsAPI';
import { useEffect, useState } from 'react';
import { BOOLEAN, DATE, DOUBLE, LONG } from 'utils/fieldTypes';
import { FacetOption, FieldFacet } from './types';
import { getUniqueListBy } from './utils';

/*
  This function hydrates a sorted array of options for the Select component
*/
const getFacetOptions = (data: FieldFacet[], fieldType: string): FacetOption[] => {
  const uniqueData = getUniqueListBy<FieldFacet>(data, 'term');

  if (fieldType === BOOLEAN) {
    return uniqueData.map((x: FieldFacet, i: number) => {
      const numberValue = Number(x.term);
      const isNumber = !isNaN(numberValue);
      const booleanValue = isNumber ? `${Boolean(numberValue)}` : x.term;

      return {
        value: booleanValue,
        id: i.toString(),
        label: booleanValue,
      };
    });
  }

  return uniqueData
    .map((x: FieldFacet, i: number) => ({
      value: x.term,
      id: i.toString(),
      label: x.term,
    }))
    .sort((a: FacetOption, b: FacetOption) => {
      if ([DATE, LONG, DOUBLE].includes(fieldType)) {
        return Number(a.value) - Number(b.value);
      } else {
        return a.value.localeCompare(b.value);
      }
    });
};

export const useFieldFacets = (field: string, fieldType: string) => {
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [facetOptions, setFacetOptions] = useState<FacetOption[]>([]);
  const [hasFailed, setHasFailed] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const getFacets = async (searchField: string, searchType: string) => {
    setHasFailed(false);
    setIsLoaded(false);
    try {
      const facets: FieldFacet[] = await fetchFieldFacets(searchField, 'user');
      const orderedFacetOptions: FacetOption[] = getFacetOptions(facets, searchType);
      setFacetOptions(orderedFacetOptions);
      setIsLoaded(true);
    } catch (ex) {
      setHasFailed(true);
    }
  };

  useEffect(() => {
    if (!field || !fieldType) return;
    setFacetOptions([]);
    (async () => {
      await getFacets(field, fieldType);
    })();
  }, [field, fieldType]);

  useEffect(() => {
    if (shouldRefetch) {
      setShouldRefetch(false);
      (async () => {
        await getFacets(field, fieldType);
      })();
    }
  }, [shouldRefetch]);

  return { facetOptions, hasFailed, isLoaded, refetch: () => setShouldRefetch(true) };
};
